/**
 * Caption visual + animation schema.
 *
 * A `TemplateConfig` is a fully serializable description of how captions look
 * and move. It is stored on projects (resolved, with a version) and consumed
 * identically by the preview canvas and the export renderer.
 */
/** Reference frame height used to express font sizes and stroke widths. */
export const REFERENCE_HEIGHT = 1920;
/**
 * Parse a hex (#rgb/#rrggbb/#rrggbbaa) or rgb()/rgba() color to 0..255 RGB.
 * Returns null when it can't be parsed (e.g. a gradient/keyword).
 */
export function parseColorRgb(color) {
    const c = color.trim();
    const hex = c.match(/^#([0-9a-fA-F]{3}|[0-9a-fA-F]{4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/);
    if (hex) {
        let h = hex[1];
        if (h.length === 3 || h.length === 4)
            h = h.split('').map((ch) => ch + ch).join('');
        return { r: parseInt(h.slice(0, 2), 16), g: parseInt(h.slice(2, 4), 16), b: parseInt(h.slice(4, 6), 16) };
    }
    const rgb = c.match(/^rgba?\(\s*([\d.]+)[ ,]+([\d.]+)[ ,]+([\d.]+)/i);
    if (rgb)
        return { r: +rgb[1], g: +rgb[2], b: +rgb[3] };
    return null;
}
/** Relative luminance 0 (black) .. 1 (white); ~0.5 is mid. (A readable-text
 *  helper `readableTextColor` already lives in hooks.ts.) */
export function colorLuminance(color) {
    const rgb = parseColorRgb(color);
    if (!rgb)
        return 0.5;
    return (0.299 * rgb.r + 0.587 * rgb.g + 0.114 * rgb.b) / 255;
}
/**
 * A stroke colour that won't blend into the fill. If the outline is (nearly) the
 * same colour as the text it wraps - e.g. a white active/spoken word inside a
 * white outline (Rainbow, Sticker) - the letters disappear. In that case return
 * a contrasting outline (dark for a light fill, light for a dark fill); otherwise
 * keep the intended stroke. `fillColor` that can't be parsed (a gradient/keyword)
 * leaves the stroke unchanged.
 */
export function contrastingStroke(strokeColor, fillColor) {
    const s = parseColorRgb(strokeColor);
    const f = parseColorRgb(fillColor);
    if (!s || !f)
        return strokeColor;
    const dist = Math.abs(s.r - f.r) + Math.abs(s.g - f.g) + Math.abs(s.b - f.b);
    if (dist > 60)
        return strokeColor; // clearly different - leave it
    return colorLuminance(fillColor) > 0.5 ? '#101216' : '#ffffff';
}
//# sourceMappingURL=style.js.map